import React, { useState, useEffect } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";

const MAX_FILES = 4;
const MAX_SIZE_MB = 25;
const ACCEPT = "image/*,video/*,audio/*";

export default function PostComposer({
  onPosted,
  defaultVisibility = "public",
}) {
  const navigate = useNavigate();

  const [mode, setMode] = useState("post"); // post | clip

  const [text, setText] = useState("");
  const [hashtag, setHashtag] = useState("");
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [posting, setPosting] = useState(false);
  const [aiAssisted, setAiAssisted] = useState(false);

  const [visibility, setVisibility] = useState(defaultVisibility || "public");

  const [clipVideo, setClipVideo] = useState(null);
  const [clipPreview, setClipPreview] = useState("");
  const [clipCaption, setClipCaption] = useState("");
  const [locationId, setLocationId] = useState(null);
const [locationName, setLocationName] = useState("");
const [locationVisibility, setLocationVisibility] =
  useState("private");
const [suggestions, setSuggestions] = useState([]);
const [suggestedName, setSuggestedName] = useState("");

const [officialName, setOfficialName] = useState("");
const [isOfficialLocation, setIsOfficialLocation] = useState(false);
const [hasCustomName, setHasCustomName] = useState(false);

  useEffect(() => {
    setVisibility(defaultVisibility || "public");
  }, [defaultVisibility]);

  useEffect(() => {
    const urls = files.map((f) => ({
      url: URL.createObjectURL(f),
      type: f.type,
      name: f.name,
      size: f.size,
    }));
    setPreviews(urls);

    return () => urls.forEach((p) => URL.revokeObjectURL(p.url));
  }, [files]);

  useEffect(() => {
    if (!clipVideo) {
      setClipPreview("");
      return;
    }

    const url = URL.createObjectURL(clipVideo);
    setClipPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [clipVideo]);

  const onPickFiles = (e) => {
    const chosen = Array.from(e.target.files || []);
    if (!chosen.length) return;

    const existing = files.length;
    const allowedCount = Math.max(0, MAX_FILES - existing);
    const slice = chosen.slice(0, allowedCount);

    const overSized = slice.find((f) => f.size > MAX_SIZE_MB * 1024 * 1024);
    if (overSized) {
      alert(`Each file must be <= ${MAX_SIZE_MB} MB.`);
      return;
    }

    setFiles((prev) => [...prev, ...slice]);
    e.target.value = "";
  };

  const onPickClip = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("video/")) {
      alert("Please select a video file.");
      e.target.value = "";
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      alert("Clip must be <= 20 MB.");
      e.target.value = "";
      return;
    }

    setClipVideo(file);
    e.target.value = "";
  };

  const removeFileAt = (idx) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx));

  const resetPostForm = () => {
  setText("");
  setHashtag("");
  setFiles([]);
  setAiAssisted(false);

  setLocationId(null);
  setLocationName("");
  setSuggestions([]);
  setSuggestedName("");
  setLocationVisibility("private");

  setOfficialName("");
  setIsOfficialLocation(false);
  setHasCustomName(false);
};

  const resetClipForm = () => {
  setClipVideo(null);
  setClipCaption("");
  setClipPreview("");
  setHashtag("");

  setLocationId(null);
  setLocationName("");
  setSuggestions([]);
  setSuggestedName("");
  setLocationVisibility("private");

  setOfficialName("");
  setIsOfficialLocation(false);
  setHasCustomName(false);
};

  const submit = async (e) => {
    e?.preventDefault();
    if (posting) return;

    if (mode === "clip") {
      if (!clipVideo) {
        alert("Select a video.");
        return;
      }

      if (!hashtag.trim()) {
        const ok = window.confirm(
          "You're uploading a clip without a hashtag.\n" +
            "This clip will appear only on your profile (not in the main feed).\n\n" +
            "Continue?"
        );
        if (!ok) return;
      }

      try {
        setPosting(true);

        const fd = new FormData();
        fd.append("video", clipVideo);
        fd.append("caption", clipCaption);
        fd.append("visibility", visibility || "public");
        
        if (locationId) {
  fd.append("location_id", locationId);
}

        const normalized = hashtag.trim().replace(/^#/, "");
        if (normalized) fd.append("hashtag", normalized);

        const { data } = await api.post("/clips", fd, {
  headers: { "Content-Type": "multipart/form-data" },
});

const me = JSON.parse(
  localStorage.getItem("user") || "{}"
);

const finalName = suggestedName.trim() || locationName;

if (
  locationId &&
  finalName &&
  finalName !== "Unknown Location"
) {
  await api.post(
    `/locations/${locationId}/suggest-name`,
    {
      userId: me.id || me.user_id,
      name: finalName,
      visibility: locationVisibility
    }
  );
}

resetClipForm();
onPosted && onPosted(data?.clip);
navigate("/clips");
      } catch (err) {
        alert(err?.response?.data?.error || err.message || "Clip upload failed");
      } finally {
        setPosting(false);
      }

      return;
    }

    if (!text.trim() && !hashtag.trim() && files.length === 0) {
      alert("Write something or attach media.");
      return;
    }

    if (!hashtag.trim()) {
      const ok = window.confirm(
        "You're posting without a hashtag.\n" +
          "This post will appear only on your profile (not in the main feed).\n\n" +
          "Continue?"
      );
      if (!ok) return;
    }

    const fd = new FormData();

    if (text) fd.append("text", text);

    if (hashtag) {
      const normalized = hashtag.trim().replace(/^#/, "");
      fd.append("hashtag", normalized);
    }

    files.forEach((f) => fd.append("media", f));
    fd.append("ai_assisted", aiAssisted ? "true" : "false");
    fd.append("visibility", visibility || "public");
    
    if (locationId) {
  fd.append("location_id", locationId);
}

    try {
      setPosting(true);
      

      const { data } = await api.post("/posts", fd, {
  headers: { "Content-Type": "multipart/form-data" },
});

const me = JSON.parse(
  localStorage.getItem("user") || "{}"
);

const finalName = suggestedName.trim() || locationName;

if (
  locationId &&
  finalName &&
  finalName !== "Unknown Location"
) {
  await api.post(
    `/locations/${locationId}/suggest-name`,
    {
      userId: me.id || me.user_id,
      name: finalName,
      visibility: locationVisibility
    }
  );
}

resetPostForm();
onPosted && onPosted(data?.post);


if (me.user_id) {
  navigate(`/u/${me.user_id}`);
} else {
  navigate("/");
}
    } catch (err) {
      alert(err?.response?.data?.error || err.message || "Post failed");
    } finally {
      setPosting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      style={{
        border: "1px solid #eee",
        borderRadius: 12,
        padding: 12,
        marginBottom: 16,
      }}
    >
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button
          type="button"
          onClick={() => setMode("post")}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #ddd",
            background: mode === "post" ? "#111" : "#fff",
            color: mode === "post" ? "#fff" : "#111",
            cursor: "pointer",
          }}
        >
          Post
        </button>

        <button
          type="button"
          onClick={() => setMode("clip")}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #ddd",
            background: mode === "clip" ? "#111" : "#fff",
            color: mode === "clip" ? "#fff" : "#111",
            cursor: "pointer",
          }}
        >
          Clip
        </button>
      </div>
      
      <div style={{ marginBottom: 8 }}>
  <button
    type="button"
    onClick={async () => {

  const me = JSON.parse(
    localStorage.getItem("user") || "{}"
  );

  if (!navigator.geolocation) {
    alert("GPS not supported");
    return;
  }

  navigator.geolocation.getCurrentPosition(

    async (position) => {

      try {

        const { data } = await api.post(
          "/footprints",
          {
            userId: me.id || me.user_id,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }
        );
        
        setLocationId(data.location.id);


const myChoiceRes =
  await api.get(
    `/locations/${data.location.id}/my-choice/${me.id || me.user_id}`
  );


const choice = myChoiceRes.data.choice;

setOfficialName(
  choice?.official_name || ""
);

setIsOfficialLocation(
  choice?.is_official || false
);

if (choice?.chosen_name) {

  setLocationName(choice.chosen_name);

  setHasCustomName(
  !!choice.official_name &&
  choice.chosen_name !== choice.official_name
);

  setLocationVisibility(
    choice.visibility || "private"
  );

} else if (choice?.official_name) {

  setLocationName(
    choice.official_name
  );

  setHasCustomName(false);

  setLocationVisibility("private");

} else {

  setLocationName(
    data.location.name
  );

  setHasCustomName(false);

  setLocationVisibility("private");

}

const suggestionsRes =
  await api.get(
    `/locations/${data.location.id}/suggestions`
  );

setSuggestions(
  suggestionsRes.data.suggestions || []
);

if (suggestionsRes.data.is_official) {
  setOfficialName(
    suggestionsRes.data.official_name
  );

  setIsOfficialLocation(true);
}

      } catch (err) {

        console.error(err);

        alert(
          err?.response?.data?.error ||
          "Failed to update location privacy"
        );

      }

    },

    (err) => {

      alert(
        `GPS Error ${err.code}: ${err.message}`
      );

    },

    {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    }

  );

}}
  >
    📍 Attach Footprint
  </button>

  {locationName && (
  <div
    style={{
      marginTop: 6,
      fontSize: 14
    }}
  >
    📍 {locationName}

{hasCustomName &&
  isOfficialLocation &&
  officialName && (
    <span style={{ color: "#666" }}>
      {" "}
      ({officialName})
    </span>
)}
   
   {locationId && locationName !== "Unknown Location" && ( 
    <div style={{ marginTop: 6 }}>
  <select
    value={locationVisibility}
    onChange={async (e) => {

  const oldVisibility = locationVisibility;
  const newVisibility = e.target.value;

  const me = JSON.parse(
    localStorage.getItem("user") || "{}"
  );

  setLocationVisibility(newVisibility);

  try {

    await api.put(
      `/locations/${locationId}/privacy`,
      {
        userId: me.id || me.user_id,
        visibility: newVisibility
      }
    );

  } catch (err) {

    setLocationVisibility(oldVisibility);

    console.error(err);

    alert(
      err?.response?.data?.error ||
      "Failed to update location privacy"
    );

  }

}}
  >
    <option value="private">
      🔒 Personal only
    </option>

    <option value="public">
      🌍 Community suggestion
    </option>
  </select>
</div>
)}

    {suggestions.length > 0 && (
      <div style={{ marginTop: 8 }}>
        <strong>
          People have suggested:
        </strong>

        {suggestions.map((s) => (
  <button
    key={s.proposed_name}
    type="button"
    onClick={() => {
      setSuggestedName(s.proposed_name);
  setLocationName(s.proposed_name);
    }}
    style={{
      display: "block",
      marginTop: 4,
      border: "1px solid #ddd",
      borderRadius: 6,
      background: "#fff",
      padding: "6px 10px",
      cursor: "pointer"
    }}
  >
    🏠 {s.proposed_name} ({s.votes})
  </button>
))}
      </div>
    )}

    {locationName && (
      <div style={{ marginTop: 8 }}>
        
        {isOfficialLocation &&
  officialName &&
  hasCustomName && (
    <button
      type="button"
      onClick={() => {

        setLocationName(officialName);
        setHasCustomName(false);
        setSuggestedName(officialName);
      }}
    >
      Use Official Name
    </button>
)}
        
        <input
  value={suggestedName}
  onChange={(e) =>
    setSuggestedName(e.target.value)
  }
  onFocus={() => {
  if (
    !suggestedName &&
    locationName &&
    locationName !== "Unknown Location"
  ) {
    setSuggestedName(locationName);
  }
}}
  placeholder={
    locationName === "Unknown Location"
      ? "Suggest a name"
      : "Change location name"
  }
/>

        <button
          type="button"
          onClick={async () => {

            if (!suggestedName.trim()) {
              return;
            }

            
const newName =
  suggestedName.trim();

setLocationName(newName);

if (isOfficialLocation) {
  setHasCustomName(
    newName !== officialName
  );
}

            const refreshed =
  await api.get(
    `/locations/${locationId}/suggestions`
  );

setSuggestions(
  refreshed.data.suggestions || []
);

if (refreshed.data.is_official) {
  setOfficialName(
    refreshed.data.official_name
  );

  setIsOfficialLocation(true);

  setHasCustomName(
    newName !== refreshed.data.official_name
  );
}

            setSuggestedName(newName);

          }}
        >
  {
    locationName === "Unknown Location"
      ? "Save"
      : "Rename"
  }
</button>
      </div>
    )}
  </div>
)}
</div>

      {mode === "clip" ? (
        <>
          <div style={{ marginBottom: 10 }}>
            <label style={{ cursor: "pointer" }}>
              <input
                type="file"
                accept="video/*"
                onChange={onPickClip}
                style={{ display: "none" }}
              />
              <span
                style={{
                  padding: "6px 10px",
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  display: "inline-block",
                }}
              >
                Select clip
              </span>
            </label>
          </div>

          <textarea
            value={clipCaption}
            onChange={(e) => setClipCaption(e.target.value)}
            placeholder="Write a caption"
            rows={3}
            style={{ width: "100%", marginBottom: 8 }}
          />

          <input
            value={hashtag}
            onChange={(e) => setHashtag(e.target.value)}
            placeholder="Optional hashtag (e.g. news)"
            style={{ width: "100%", marginBottom: 6 }}
          />

          {!hashtag.trim() && (
            <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
              Tip: Without a hashtag, this clip will only appear on your profile, not in the main feed.
            </div>
          )}

          <select
            value={visibility}
            onChange={(e) => setVisibility(e.target.value)}
            style={{ marginBottom: 8 }}
          >
            <option value="public">🌍 Public</option>
            <option value="friends">👥 Friends</option>
            <option value="private">🔒 Only me</option>
          </select>

          {clipPreview && (
            <div
              style={{
                border: "1px solid #eee",
                borderRadius: 8,
                padding: 6,
                marginBottom: 10,
              }}
            >
              <video
                src={clipPreview}
                controls
                style={{
                  width: "100%",
                  maxHeight: 320,
                  objectFit: "cover",
                  borderRadius: 8,
                }}
              />
            </div>
          )}
        </>
      ) : (
        <>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="What's happening?"
            rows={3}
            style={{ width: "100%", marginBottom: 8 }}
          />
          
          

          <input
            value={hashtag}
            onChange={(e) => setHashtag(e.target.value)}
            placeholder="Optional hashtag (e.g. news)"
            style={{ width: "100%", marginBottom: 6 }}
          />

          {!hashtag.trim() && (
            <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
              Tip: Without a hashtag, this post will only appear on your profile, not in the main feed.
            </div>
          )}

          <select
            value={visibility}
            onChange={(e) => setVisibility(e.target.value)}
            style={{ marginBottom: 8 }}
          >
            <option value="public">🌍 Public</option>
            <option value="friends">👥 Friends</option>
            <option value="private">🔒 Only me</option>
          </select>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 14,
              margin: "6px 0 10px",
            }}
          >
            <input
              type="checkbox"
              checked={aiAssisted}
              onChange={(e) => setAiAssisted(e.target.checked)}
            />
            <span>
              Mark as <strong>AI-assisted</strong>
            </span>
          </label>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <label style={{ cursor: "pointer" }}>
              <input
                type="file"
                accept={ACCEPT}
                multiple
                onChange={onPickFiles}
                style={{ display: "none" }}
              />
              <span
                style={{
                  padding: "6px 10px",
                  border: "1px solid #ddd",
                  borderRadius: 8,
                }}
              >
                Attach media ({files.length}/{MAX_FILES})
              </span>
            </label>

            {files.length > 0 && (
              <button
                type="button"
                onClick={() => setFiles([])}
                style={{ padding: "6px 10px" }}
              >
                Clear media
              </button>
            )}
          </div>

          {previews.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                gap: 8,
                marginBottom: 8,
              }}
            >
              {previews.map((p, i) => (
                <div
                  key={i}
                  style={{
                    position: "relative",
                    border: "1px solid #eee",
                    borderRadius: 8,
                    padding: 4,
                  }}
                >
                  {p.type.startsWith("image/") ? (
                    <img
                      src={p.url}
                      alt=""
                      style={{
                        width: "100%",
                        height: 140,
                        objectFit: "cover",
                        borderRadius: 6,
                      }}
                    />
                  ) : p.type.startsWith("video/") ? (
                    <video
                      src={p.url}
                      controls
                      style={{
                        width: "100%",
                        height: 140,
                        objectFit: "cover",
                        borderRadius: 6,
                      }}
                    />
                  ) : p.type.startsWith("audio/") ? (
                    <audio src={p.url} controls style={{ width: "100%" }} />
                  ) : (
                    <div style={{ padding: 8, fontSize: 12 }}>{p.name}</div>
                  )}

                  <button
                    type="button"
                    onClick={() => removeFileAt(i)}
                    style={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      border: "none",
                      background: "#fff",
                      borderRadius: 14,
                      padding: "2px 6px",
                      cursor: "pointer",
                      boxShadow: "0 0 0 1px #ddd",
                    }}
                    aria-label="Remove"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" disabled={posting}>
          {posting
            ? mode === "clip"
              ? "Uploading..."
              : "Posting..."
            : mode === "clip"
            ? "Upload Clip"
            : "Post"}
        </button>
      </div>
    </form>
  );
}